import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateAnalyticsQueriesForAccount } from '~/hooks/analyticsStatAccess/invalidateAnalyticsQueriesForAccount';
import { staffConfigModeQueryParams } from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import { statAccessColumnCatalogPath } from '~/hooks/analyticsStatAccess/statAccessApiPaths';
import {
  applyStaffStatColumnCatalogBodySchema,
  applyStaffStatColumnCatalogSuccessSchema,
  type ApplyStaffStatColumnCatalogBody,
} from '~/hooks/queries/useStaffStatColumnCatalogQuery/staffStatColumnCatalogSchema';
import type { Genie2ReportingMode } from '~/utils/analyticsReportingMode';
import { customFetch } from '~/utils/customFetch';
import {
  AnalyticsStatsApiError,
  flattenAnalyticsStatsErrorResponse,
} from '~/views/Dashboard/AnalyticsStats/analyticsStatsErrorUtils';

export type ApplyStaffStatColumnCatalogParams = {
  accountType: 'client' | 'partner';
  accountId: string;
  body: ApplyStaffStatColumnCatalogBody;
  /** Staff Client Manager apply — adds `staff=1` so Ergon treats the write as staff mode. */
  asStaff?: boolean;
  /** Parent hub — uses children mount; do not send `staff=1`. */
  parentAccountId?: string | null;
  /** Pre-seed query param only; omit when live. Never merged into the body. */
  queryAnalyticsReportingMode?: Genie2ReportingMode | null;
};

const applyStaffStatColumnCatalog = async (
  params: ApplyStaffStatColumnCatalogParams,
): Promise<{ touchedStatKeys: string[] }> => {
  const {
    accountType,
    accountId,
    body,
    asStaff = false,
    parentAccountId,
    queryAnalyticsReportingMode,
  } = params;
  const parsedBody = applyStaffStatColumnCatalogBodySchema.parse(body);

  const { data, status } = await customFetch<unknown>(
    statAccessColumnCatalogPath(accountType, accountId, parentAccountId),
    {
      method: 'PUT',
      body: JSON.stringify(parsedBody),
      headers: { 'Content-Type': 'application/json' },
      // Query param only while Classic Genie pre-seed; live accounts omit it.
      // Body may include analyticsReportingMode for staff client assignment.
      params: staffConfigModeQueryParams(
        queryAnalyticsReportingMode,
        asStaff && !parentAccountId ? { staff: '1' } : undefined,
      ),
      useNodeEndpoint: true,
      throwOnHTTPError: false,
    },
  );

  if (status >= 400) {
    throw new AnalyticsStatsApiError(flattenAnalyticsStatsErrorResponse(data));
  }

  const parsed = applyStaffStatColumnCatalogSuccessSchema.parse(data);
  return { touchedStatKeys: parsed.touchedStatKeys };
};

export function useApplyStaffStatColumnCatalogMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: applyStaffStatColumnCatalog,
    mutationKey: ['staff', 'apply-stat-column-catalog'],
    onSuccess: (_, variables) => {
      invalidateAnalyticsQueriesForAccount(queryClient, {
        accountType: variables.accountType,
        accountId: variables.accountId,
        parentAccountId: variables.parentAccountId,
      });
    },
  });
}
